import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { formatDateTime } from '../../util/datetime';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

import * as releasesApi from '../../api/captureAppReleases';
import type { CaptureAppRelease } from '../../api/captureAppReleases';
import { extractErrorMessage } from '../../api/errors';
import { SortHeader } from '../../components/SortHeader';
import { useTableSort, sortRows } from '../../util/tableSort';
import releaseNotesData from '../../data/releaseNotes.json';
import { readApkVersion } from '../../util/apkInfo';

type ReleaseSortKey =
  | 'is_current'
  | 'version'
  | 'uploaded_at'
  | 'size_bytes'
  | 'sha256'
  | 'notes';

/** Committed release-notes catalogue (tags like "v0.7.2"), for autofill. */
const RELEASE_NOTES = releaseNotesData as { tag: string; name?: string; body?: string }[];

/**
 * Superadmin capture-app release catalogue.
 *
 *  - Upload dialog: pick an APK + version + optional notes; the page
 *    POSTs to create a pending row, PUTs the bytes to the presigned
 *    S3 URL, then POSTs confirm with promote_to_current set so the
 *    just-uploaded build is what every tenant sees.
 *  - Table: one row per upload, newest first. Current is starred + a
 *    success chip; non-current rows can be promoted (Star toggle) or
 *    deleted. The current row's delete is blocked server-side with a
 *    409 — the UI also disables it so the operator gets the right
 *    affordance.
 *
 * Replaces the "ask Reto for the link" capture-app distribution path.
 * Until the app is on Google Play, this is the source of truth for
 * which APK every staff device installs.
 */

function formatBytes(n: number | null): string {
  if (n === null) return '—';
  const mb = n / (1024 * 1024);
  if (mb < 1) return `${(n / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(1)} MB`;
}

export function CaptureAppReleases({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const [releases, setReleases] = useState<CaptureAppRelease[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Newest-first by default (matches the API order); every data column
  // is click-to-sort.
  const sort = useTableSort<ReleaseSortKey>('uploaded_at', 'desc');

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setReleases(await releasesApi.listCaptureAppReleases());
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'failed to load releases'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const uploadButton = (
    <Button variant="contained" onClick={() => setUploadOpen(true)}>
      Upload APK
    </Button>
  );

  return (
    <Stack spacing={3}>
      {actionsSlot ? (
        createPortal(uploadButton, actionsSlot)
      ) : (
        <Stack
          direction="row"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="flex-start"
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Capture-app releases</Typography>
              <Typography variant="body2" color="text.secondary">
                Signed Android APKs distributed to staff devices via the
                tenant Devices page install QR. The starred row is the
                "current" build every tenant downloads.
              </Typography>
            </Stack>
          )}
          {uploadButton}
        </Stack>
      )}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {releases && <MinVersionCard releases={releases} />}

      <Card>
        {!releases && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {releases && releases.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No releases yet. Click <strong>Upload APK</strong> to ship
              the first build.
            </Typography>
          </Box>
        )}

        {releases && releases.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortHeader
                    label="Current"
                    col="is_current"
                    sort={sort}
                    sx={{ width: 48 }}
                  />
                  <SortHeader label="Version" col="version" sort={sort} />
                  <SortHeader label="Uploaded" col="uploaded_at" sort={sort} />
                  <SortHeader label="Size" col="size_bytes" sort={sort} />
                  <SortHeader label="SHA-256" col="sha256" sort={sort} />
                  <SortHeader label="Notes" col="notes" sort={sort} />
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortRows(
                  releases,
                  (r) => {
                    switch (sort.orderBy) {
                      case 'is_current':
                        return r.is_current;
                      case 'version':
                        return r.version;
                      case 'uploaded_at':
                        return r.uploaded_at;
                      case 'size_bytes':
                        return r.size_bytes;
                      case 'sha256':
                        return r.sha256;
                      case 'notes':
                        return r.notes;
                    }
                  },
                  sort.order,
                ).map((r) => (
                  <ReleaseRow key={r.id} release={r} onChanged={load} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <UploadDialog
        open={uploadOpen}
        latestVersion={releases?.[0]?.version}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          void load();
        }}
      />
    </Stack>
  );
}

function ReleaseRow({
  release,
  onChanged,
}: {
  release: CaptureAppRelease;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shaShort = release.sha256 ? release.sha256.slice(0, 12) : null;

  const handlePromote = async () => {
    setBusy(true);
    setError(null);
    try {
      await releasesApi.promoteCaptureAppRelease(release.id);
      onChanged();
    } catch (err) {
      setError(extractErrorMessage(err, 'failed to promote'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await releasesApi.deleteCaptureAppRelease(release.id);
      onChanged();
    } catch (err) {
      setError(extractErrorMessage(err, 'failed to delete'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {release.is_current ? (
            <Tooltip title="Current release">
              <StarIcon color="warning" fontSize="small" />
            </Tooltip>
          ) : (
            <Tooltip
              title={
                release.sha256
                  ? 'Promote to current'
                  : 'Confirm the upload before promoting'
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={busy || release.sha256 === null}
                  onClick={() => void handlePromote()}
                  aria-label={`Promote ${release.version}`}
                >
                  <StarOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {release.version}
            </Typography>
            {release.is_current && (
              <Chip label="current" color="success" size="small" />
            )}
            {release.sha256 === null && (
              <Chip label="pending confirm" color="warning" size="small" />
            )}
          </Stack>
        </TableCell>
        <TableCell>{formatDateTime(release.uploaded_at)}</TableCell>
        <TableCell>{formatBytes(release.size_bytes)}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {shaShort ?? '—'}
        </TableCell>
        <TableCell>
          {release.notes && (
            <Typography variant="body2" color="text.secondary">
              {release.notes}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Tooltip
            title={
              release.is_current
                ? 'Promote another release first'
                : 'Delete release'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={busy || release.is_current}
                onClick={() => void handleDelete()}
                aria-label={`Delete ${release.version}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      {error && (
        <TableRow>
          <TableCell colSpan={7} sx={{ pt: 0 }}>
            <Alert severity="error">{error}</Alert>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function UploadDialog({
  open,
  latestVersion,
  onClose,
  onUploaded,
}: {
  open: boolean;
  latestVersion?: string;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [promote, setPromote] = useState(true);
  const [phase, setPhase] = useState<'idle' | 'create' | 'put' | 'confirm'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);
  // versionName read out of the picked APK's manifest (null = couldn't read).
  const [apkVersion, setApkVersion] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const busy = phase !== 'idle';
  const canSubmit = file !== null && version.trim().length > 0 && !busy;
  // Warn if the operator typed over a version that disagrees with the APK.
  const versionMismatch =
    apkVersion !== null && version.trim() !== '' && version.trim() !== apkVersion;

  // On pick, read versionName from the APK (no hand-typing) + pre-fill the
  // notes from the committed release-notes for that tag (ROADMAP
  // APK-UPLOAD-AUTOFILL). Falls back silently to manual entry on any problem.
  const handleFile = async (f: File | null) => {
    setFile(f);
    setApkVersion(null);
    if (!f) return;
    const info = await readApkVersion(f);
    if (!info?.versionName) return;
    setApkVersion(info.versionName);
    setVersion(info.versionName);
    const tag = `v${info.versionName}`;
    const match = RELEASE_NOTES.find(
      (r) => r.tag === tag || r.tag === info.versionName,
    );
    if (match?.body) setNotes(match.body);
  };

  const reset = () => {
    setFile(null);
    setVersion('');
    setNotes('');
    setPromote(true);
    setPhase('idle');
    setError(null);
    setApkVersion(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !file) return;
    setError(null);
    try {
      setPhase('create');
      const created = await releasesApi.createCaptureAppRelease({
        version: version.trim(),
        size_bytes: file.size,
        notes: notes.trim() || undefined,
      });
      setPhase('put');
      await releasesApi.putApkToPresignedUrl(
        created.presigned_put_url, file,
      );
      setPhase('confirm');
      await releasesApi.confirmCaptureAppRelease(
        created.release.id, promote,
      );
      reset();
      onUploaded();
    } catch (err) {
      setError(extractErrorMessage(err, 'upload failed'));
      setPhase('idle');
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>Upload capture-app APK</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              component="label"
              disabled={busy}
            >
              {file ? file.name : 'Choose APK file'}
              <input
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                hidden
                ref={fileInputRef}
                onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {file && (
              <Typography variant="caption" color="text.secondary">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
                {apkVersion && ` · versionName ${apkVersion} (read from APK)`}
              </Typography>
            )}
            <TextField
              label="Version"
              placeholder={latestVersion ? `latest is ${latestVersion}` : 'e.g. 0.5.0'}
              required
              fullWidth
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              disabled={busy}
              error={versionMismatch}
              helperText={
                versionMismatch
                  ? `Doesn't match the APK's baked versionName (${apkVersion}).`
                  : apkVersion
                    ? 'Auto-read from the APK manifest; edit only if you must.'
                    : "Must match the release tag / package.json version — that drives the APK's versionName too, so the in-app update check stays in sync."
              }
            />
            <TextField
              label="Release notes (optional)"
              fullWidth
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
            />
            <Box>
              <label>
                <input
                  type="checkbox"
                  checked={promote}
                  onChange={(e) => setPromote(e.target.checked)}
                  disabled={busy}
                />
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Promote to current after upload
                </Typography>
              </label>
            </Box>
            {phase !== 'idle' && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {phase === 'create' && 'Creating release row…'}
                  {phase === 'put' && 'Uploading APK to S3…'}
                  {phase === 'confirm' && 'Computing SHA-256…'}
                </Typography>
                <LinearProgress sx={{ mt: 0.5 }} />
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}


/**
 * Minimum supported capture-app version (2026-10-08). A phone below it
 * can't start a recording until it installs the current release; a merely
 * newer release only shows a banner. Raise it deliberately — a data bug or a
 * breaking server change — since every phone below it stops recording.
 */
function MinVersionCard({ releases }: { releases: CaptureAppRelease[] }) {
  const [saved, setSaved] = useState<string | null | undefined>(undefined);
  const [choice, setChoice] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = releasesApi.minVersionOptions(releases);

  useEffect(() => {
    releasesApi
      .getCaptureAppMinVersion()
      .then((v) => {
        setSaved(v);
        setChoice(v ?? '');
      })
      .catch((err) => setError(extractErrorMessage(err, 'failed to load the minimum version')));
  }, []);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const v = await releasesApi.setCaptureAppMinVersion(choice || null);
      setSaved(v);
    } catch (err) {
      setError(extractErrorMessage(err, 'failed to save the minimum version'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">Minimum supported version</Typography>
        <Typography variant="body2" color="text.secondary">
          Phones running an older app can't start a recording until they install the
          current release (they see a red &ldquo;Update required&rdquo; banner). A newer
          release on its own never blocks anyone. Raise this only for a real reason, such
          as a data bug or a breaking server change.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Minimum"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            sx={{ minWidth: 200 }}
            disabled={saved === undefined || busy}
          >
            <MenuItem value="">None — every version may record</MenuItem>
            {options.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            onClick={() => void save()}
            disabled={saved === undefined || busy || (choice || null) === saved}
          >
            Save
          </Button>
          <Typography variant="body2" color="text.secondary">
            {saved === undefined ? '' : saved ? `Currently ${saved}` : 'Currently none'}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}
